import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildSpeakableSchema } from "@/lib/schema";

export const Route = createFileRoute("/en/company/type/")({
  loader: async () => {
    const { COMPANY_ENTITY_TYPES } = await import("@/content/company-corpus/company-types");
    return { items: COMPANY_ENTITY_TYPES.map((x) => ({ slug: x.slug, title: x.titleEn, desc: x.whoEn })) };
  },
  component: CompanyTypeHubEn,
  head: ({ loaderData }) => {
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `Business Entity Types in Thailand — ${loaderData?.items.length ?? 0} Structures Compared`,
        description:
          "Compare Thai business structures for foreign investors: Thai-majority limited company, Foreign Business Licence, US Treaty of Amity, BOI promotion, branch office, representative office and IBC — capital thresholds, documents and official sources.",
        path: "/en/company/type",
        alternates: [{ lang: "th", path: "/company/type" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(buildSpeakableSchema("/en/company/type")),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Business Entity Types", url: `${site}/en/company/type` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            itemListElement: (loaderData?.items ?? []).map((t, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: t.title,
              url: `${site}/en/company/type/${t.slug}`,
            })),
          }),
        },
      ],
    };
  },
});

function CompanyTypeHubEn() {
  const { items } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-5xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>Business Entity Types</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          Business Structures for Operating in Thailand — {items.length} Options
        </h1>
        <p className="mb-8 max-w-3xl text-muted-foreground">
          Each page sets out who the structure suits, the capital and shareholding conditions, the documents required,
          the filing sequence, the deciding authority and the mistakes that get files rejected. Content follows the
          Civil and Commercial Code, the Foreign Business Act B.E. 2542 (1999) and published guidance from the DBD,
          the Revenue Department and the BOI. Every licence or promotion is granted at the authority's discretion.
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          {items.map((t: { slug: string; title: string; desc: string }) => (
            <a key={t.slug} href={`/en/company/type/${t.slug}`} className="rounded-lg border bg-card p-5 transition hover:border-gold">
              <h2 className="mb-2 text-lg font-semibold">{t.title}</h2>
              <p className="text-sm text-muted-foreground">{t.desc}</p>
            </a>
          ))}
        </div>
        <section className="mt-10 rounded-lg border bg-muted/30 p-5">
          <h2 className="mb-2 text-xl font-bold">Not sure which structure fits?</h2>
          <p className="text-sm text-muted-foreground">
            Tell our team your business activity, shareholder nationalities and hiring plan by phone, LINE or email and
            we will compare the conditions of each route with you. We do not set up nominee shareholding structures.
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">Contact us</a>
        </section>
        <p className="mt-8 text-xs text-muted-foreground">
          Information current as of August 2026 — reconfirm conditions and official fees with the DBD before filing.
        </p>
        <p className="mt-4 text-sm">
          Read the FAQ: <a href="/en/company/faq" className="underline hover:text-gold">Company registration FAQ</a> ·{" "}
          <a href="/en/work-permit/type" className="underline hover:text-gold">Work permit routes</a>
        </p>
      </main>
      <SiteFooter />
    </>
  );
}